/**
 * Auth slice — holds the access token in Redux (memory only).
 *
 * The access token is NEVER written to localStorage/sessionStorage.
 * It lives in Redux state only, so it is cleared on page refresh. The backend
 * restores it from an opaque HttpOnly browser-session cookie at app startup.
 *
 * What IS persisted (localStorage):
 *   - The non-sensitive user profile object (id, username, email, role).
 *     This carries no auth power on its own.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../shared/api/client';

// ── ASYNC THUNKS ──────────────────────────────────────────────────────────────

export const login = createAsyncThunk(
    'auth/login',
    async ({ email, password, twoFactorToken }, { rejectWithValue }) => {
        try {
            const { data } = await apiClient.post('/auth/login', {
                email,
                password,
                twoFactorToken
            });
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || 'Login failed.');
        }
    }
);

export const restoreSession = createAsyncThunk(
    'auth/restoreSession',
    async (_, { rejectWithValue }) => {
        try {
            // A 429 is temporary: honor the server delay once, then stop so a
            // reload cannot create an unbounded restore request storm.
            for (let attempt = 0; attempt < 2; attempt += 1) {
                try {
                    const { data } = await apiClient.post('/auth/session', {}, {
                        // A missing/expired browser session is an expected bootstrap result,
                        // not an authenticated API failure that should redirect again.
                        skipAuthRedirect: true
                    });
                    return data;
                } catch (err) {
                    if (err.response?.status !== 429 || attempt === 1) throw err;
                    const retryAfter = Number(err.response.headers?.['retry-after']);
                    const delay = Number.isFinite(retryAfter)
                        ? Math.min(30000, Math.max(1000, retryAfter * 1000))
                        : 1000;
                    await new Promise(resolve => setTimeout(resolve, delay));
                }
            }
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || 'No active session.');
        }
    },
    {
        // Redux state, rather than a component ref, prevents duplicate restores
        // when React StrictMode re-runs startup effects.
        condition: (_, { getState }) => {
            const auth = getState().auth;
            return !auth.authInitialized && !auth.authInitializing;
        }
    }
);

export const logoutSession = createAsyncThunk(
    'auth/logoutSession',
    async (_, { dispatch }) => {
        try {
            await apiClient.post('/auth/logout', {}, { skipAuthRedirect: true });
        } finally {
            // Clearing Redux happens even if the server is unavailable. The
            // request above is best-effort, while the local logout is certain.
            dispatch(logout());
        }
    }
);

export const refreshToken = createAsyncThunk(
    'auth/refresh',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await apiClient.post('/auth/refresh');
            return data.token;
        } catch (err) {
            return rejectWithValue('Session expired. Please log in again.');
        }
    }
);

export const fetchCurrentUser = createAsyncThunk(
    'auth/me',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await apiClient.get('/auth/me');
            return data.user;
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || 'Failed to fetch user.');
        }
    }
);

// ── SLICE ─────────────────────────────────────────────────────────────────────

const PERSISTED_USER_KEY = 'sp_user';

function loadPersistedUser() {
    try {
        const raw = localStorage.getItem(PERSISTED_USER_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

const authSlice = createSlice({
    name: 'auth',
    initialState: {
        // Access token lives in memory only
        token: null,
        // Non-sensitive user profile can be persisted
        user: loadPersistedUser(),
        loading: false,
        error: null,
        requiresTwoFactor: false,
        authInitialized: false,
        authInitializing: false
    },
    reducers: {
        // Called by api client when a token is obtained (e.g., after refresh)
        setToken(state, action) {
            state.token = action.payload;
        },
        logout(state) {
            state.token = null;
            state.user = null;
            state.error = null;
            state.requiresTwoFactor = false;
            state.authInitialized = true;
            state.authInitializing = false;
            localStorage.removeItem(PERSISTED_USER_KEY);
        },
        clearError(state) {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        // login
        builder
            .addCase(login.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.requiresTwoFactor = false;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload.requiresTwoFactor) {
                    state.requiresTwoFactor = true;
                    return;
                }
                state.authInitialized = true;
                state.token = action.payload.token;
                state.user = action.payload.user;
                // Persist non-sensitive profile only
                localStorage.setItem(PERSISTED_USER_KEY, JSON.stringify(action.payload.user));
            })
            .addCase(login.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Browser-session bootstrap
        builder
            .addCase(restoreSession.pending, (state) => {
                state.authInitializing = true;
                state.error = null;
            })
            .addCase(restoreSession.fulfilled, (state, action) => {
                state.authInitializing = false;
                state.authInitialized = true;
                state.token = action.payload.token;
                state.user = action.payload.user;
                localStorage.setItem(PERSISTED_USER_KEY, JSON.stringify(action.payload.user));
            })
            .addCase(restoreSession.rejected, (state) => {
                state.authInitializing = false;
                state.authInitialized = true;
                state.token = null;
                state.user = null;
                state.error = null;
                localStorage.removeItem(PERSISTED_USER_KEY);
            });

        // refresh
        builder
            .addCase(refreshToken.fulfilled, (state, action) => {
                state.token = action.payload;
            })
            .addCase(refreshToken.rejected, (state) => {
                state.token = null;
                state.user = null;
                localStorage.removeItem(PERSISTED_USER_KEY);
            });

        // fetch current user
        builder
            .addCase(fetchCurrentUser.fulfilled, (state, action) => {
                state.user = action.payload;
                localStorage.setItem(PERSISTED_USER_KEY, JSON.stringify(action.payload));
            });
    }
});

export const { setToken, logout, clearError } = authSlice.actions;

// ── SELECTORS ─────────────────────────────────────────────────────────────────

export const selectToken = (state) => state.auth.token;
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => !!state.auth.token;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectRequiresTwoFactor = (state) => state.auth.requiresTwoFactor;
export const selectAuthInitialized = (state) => state.auth.authInitialized;
export const selectAuthInitializing = (state) => state.auth.authInitializing;
export const selectUserRole = (state) => state.auth.user?.role;

export default authSlice.reducer;
